import * as THREE from 'three';

const ARCH_HEIGHT = 4.4;
const ARCH_WIDTH = 2.6;
const FRAME_RADIUS = 0.16;
const PARTICLE_COUNT = 96;

/**
 * Vibejam exit portal placed inside the station interior. Designed to
 * read minimal + premium against the cozy station palette — a thin
 * neon archway with a soft inner shimmer plus a small floor sign.
 *
 * Player walks through the archway → game redirects to the Vibejam
 * 2026 webring with continuity params filled in.
 */
export class StationExitPortal {
  readonly object3D: THREE.Group;
  readonly position: THREE.Vector3;
  readonly facing: number;

  private readonly innerMat: THREE.MeshBasicMaterial;
  private readonly archEmissiveMat: THREE.MeshStandardMaterial;
  private readonly haloMat: THREE.MeshBasicMaterial;
  private readonly particleGeo: THREE.BufferGeometry;
  private readonly particleMat: THREE.PointsMaterial;
  private readonly textureRefs: THREE.Texture[] = [];
  private readonly disposables: Array<{
    geometry?: THREE.BufferGeometry;
    material?: THREE.Material | THREE.Material[];
  }> = [];
  private elapsed = 0;
  private disposed = false;

  /** Half-extents of the trigger box, in local space (X right, Y up,
   *  Z forward). Player must be inside all three to count as having
   *  walked through. Forward axis aligns with `facing`. */
  private readonly triggerHalf = new THREE.Vector3(
    ARCH_WIDTH * 0.55,
    ARCH_HEIGHT * 0.55,
    0.65
  );

  constructor(opts: { position: THREE.Vector3; facing: number }) {
    this.position = opts.position.clone();
    this.facing = opts.facing;
    this.object3D = new THREE.Group();
    this.object3D.position.copy(this.position);
    this.object3D.rotation.y = this.facing;

    this.archEmissiveMat = new THREE.MeshStandardMaterial({
      color: 0x9fd9ff,
      emissive: 0x6ac0ff,
      emissiveIntensity: 1.4,
      roughness: 0.35,
      metalness: 0.55
    });
    this.innerMat = new THREE.MeshBasicMaterial({
      color: 0xb4e3ff,
      transparent: true,
      opacity: 0.32,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    this.haloMat = new THREE.MeshBasicMaterial({
      color: 0x9fd9ff,
      transparent: true,
      opacity: 0.18,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    this.buildArchFrame();
    this.buildInnerSurface();
    this.buildFloorHalo();
    this.buildPlinthAndSign();

    // Particle ribbon drifting inside the archway.
    this.particleGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      positions[i * 3 + 0] = (Math.random() - 0.5) * ARCH_WIDTH * 0.95;
      positions[i * 3 + 1] = Math.random() * (ARCH_HEIGHT - 0.3);
      positions[i * 3 + 2] = (Math.random() - 0.5) * 0.15;
    }
    this.particleGeo.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3)
    );
    this.particleMat = new THREE.PointsMaterial({
      color: 0xb4e3ff,
      size: 0.06,
      transparent: true,
      opacity: 0.85,
      depthWrite: false
    });
    const particles = new THREE.Points(this.particleGeo, this.particleMat);
    this.object3D.add(particles);
    this.disposables.push({
      geometry: this.particleGeo,
      material: this.particleMat
    });

    // Soft point light embedded in the arch — sells the glow without
    // needing post-processing. Falls off quickly so it doesn't paint
    // the whole alcove.
    const lamp = new THREE.PointLight(0x9fd9ff, 0.9, 8, 2.2);
    lamp.position.set(0, ARCH_HEIGHT * 0.6, 0);
    this.object3D.add(lamp);
  }

  /** Animate the inner shimmer + drifting motes. Cheap. */
  update(dt: number): void {
    if (this.disposed) return;
    this.elapsed += dt;
    const pulse = 0.5 + 0.5 * Math.sin(this.elapsed * 1.3);
    this.innerMat.opacity = 0.22 + 0.2 * pulse;
    this.haloMat.opacity = 0.12 + 0.1 * pulse;
    this.archEmissiveMat.emissiveIntensity = 1.1 + 0.5 * pulse;

    const attr = this.particleGeo.getAttribute('position') as THREE.BufferAttribute;
    const positions = attr.array as Float32Array;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      let y = positions[i * 3 + 1]!;
      y += dt * 0.55;
      if (y > ARCH_HEIGHT - 0.3) y = 0.05;
      positions[i * 3 + 1] = y;
    }
    attr.needsUpdate = true;
  }

  /** True when the player point sits inside the archway trigger box. */
  containsPoint(worldPoint: THREE.Vector3): boolean {
    const local = worldPoint.clone().sub(this.position);
    // Rotate `local` by `-facing` around Y so we can compare against
    // axis-aligned half-extents.
    const cos = Math.cos(-this.facing);
    const sin = Math.sin(-this.facing);
    const x = local.x * cos - local.z * sin;
    const z = local.x * sin + local.z * cos;
    const y = local.y;
    return (
      Math.abs(x) <= this.triggerHalf.x &&
      Math.abs(z) <= this.triggerHalf.z &&
      y >= -0.4 &&
      y <= ARCH_HEIGHT + 0.4
    );
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const d of this.disposables) {
      d.geometry?.dispose();
      if (Array.isArray(d.material)) {
        for (const m of d.material) m.dispose();
      } else {
        d.material?.dispose();
      }
    }
    for (const t of this.textureRefs) t.dispose();
    this.disposables.length = 0;
    this.object3D.parent?.remove(this.object3D);
  }

  // ---- private builders ---------------------------------------------

  private buildArchFrame(): void {
    // Two vertical tubes plus a top arc — built as cylinders + a torus
    // arc segment so the archway feels like a deliberate piece of
    // station architecture and not a stretched primitive.
    const sideGeo = new THREE.CylinderGeometry(
      FRAME_RADIUS,
      FRAME_RADIUS * 1.1,
      ARCH_HEIGHT,
      18
    );
    const left = new THREE.Mesh(sideGeo, this.archEmissiveMat);
    left.position.set(-ARCH_WIDTH / 2, ARCH_HEIGHT / 2, 0);
    this.object3D.add(left);
    const right = left.clone();
    right.position.x = ARCH_WIDTH / 2;
    this.object3D.add(right);
    this.disposables.push({
      geometry: sideGeo,
      material: this.archEmissiveMat
    });

    // Top arc — half torus rotated so its open side faces down.
    const arcGeo = new THREE.TorusGeometry(
      ARCH_WIDTH / 2,
      FRAME_RADIUS,
      14,
      48,
      Math.PI
    );
    const arc = new THREE.Mesh(arcGeo, this.archEmissiveMat);
    arc.rotation.z = 0;
    arc.position.set(0, ARCH_HEIGHT, 0);
    this.object3D.add(arc);
    this.disposables.push({ geometry: arcGeo });
  }

  private buildInnerSurface(): void {
    // Rounded rectangle plane filling the archway opening. Built from
    // a plane + thin bevel ring so the inner shimmer reads like a
    // membrane sitting in the frame.
    const planeGeo = new THREE.PlaneGeometry(
      ARCH_WIDTH - FRAME_RADIUS * 2,
      ARCH_HEIGHT - FRAME_RADIUS * 2
    );
    const plane = new THREE.Mesh(planeGeo, this.innerMat);
    plane.position.set(0, (ARCH_HEIGHT - FRAME_RADIUS * 2) / 2, 0);
    this.object3D.add(plane);
    this.disposables.push({ geometry: planeGeo, material: this.innerMat });
  }

  private buildFloorHalo(): void {
    const haloGeo = new THREE.RingGeometry(
      ARCH_WIDTH * 0.6,
      ARCH_WIDTH * 1.2,
      32
    );
    const halo = new THREE.Mesh(haloGeo, this.haloMat);
    halo.rotation.x = -Math.PI / 2;
    halo.position.y = 0.04;
    this.object3D.add(halo);
    this.disposables.push({ geometry: haloGeo, material: this.haloMat });
  }

  private buildPlinthAndSign(): void {
    // Plinth + sign sit BESIDE the arch (right-hand side from the
    // player's approach), well clear of the right pillar (x = 1.3 +
    // frame radius) and pulled forward a bit so the sign card never
    // intersects the inner membrane plane at z = 0.
    const SIDE_X = 2.65;
    const SIDE_Z = 0.65;

    const plinthGeo = new THREE.BoxGeometry(0.7, 0.22, 0.7);
    const plinthMat = new THREE.MeshStandardMaterial({
      color: 0x2c3247,
      roughness: 0.55,
      metalness: 0.4
    });
    const plinth = new THREE.Mesh(plinthGeo, plinthMat);
    plinth.position.set(SIDE_X, 0.11, SIDE_Z);
    this.object3D.add(plinth);
    this.disposables.push({ geometry: plinthGeo, material: plinthMat });

    // Light strip on the plinth's front face (faces +Z toward the
    // player approach).
    const stripGeo = new THREE.PlaneGeometry(0.5, 0.06);
    const stripMat = new THREE.MeshBasicMaterial({
      color: 0xb4e3ff,
      transparent: true,
      opacity: 0.85
    });
    const strip = new THREE.Mesh(stripGeo, stripMat);
    strip.position.set(SIDE_X, 0.11, SIDE_Z + 0.351);
    this.object3D.add(strip);
    this.disposables.push({ geometry: stripGeo, material: stripMat });

    // Sign — vertical card raised on a thin pole. Faces the player as
    // they approach the arch from the atrium.
    const signTexture = makeSignTexture(
      'VIBEJAM PORTAL',
      'Step through to leave Starfall and visit other Vibejam 2026 games.'
    );
    this.textureRefs.push(signTexture);
    const signGeo = new THREE.PlaneGeometry(1.6, 0.76);
    const signMat = new THREE.MeshBasicMaterial({
      map: signTexture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    const sign = new THREE.Mesh(signGeo, signMat);
    sign.position.set(SIDE_X, 1.85, SIDE_Z);
    this.object3D.add(sign);
    this.disposables.push({ geometry: signGeo, material: signMat });

    // Thin support pole rising from the plinth to the sign.
    const strutGeo = new THREE.CylinderGeometry(0.025, 0.025, 1.6, 10);
    const strutMat = new THREE.MeshStandardMaterial({
      color: 0x4a4d5d,
      roughness: 0.5,
      metalness: 0.6
    });
    const strut = new THREE.Mesh(strutGeo, strutMat);
    strut.position.set(SIDE_X, 1.0, SIDE_Z);
    this.object3D.add(strut);
    this.disposables.push({ geometry: strutGeo, material: strutMat });
  }
}

function makeSignTexture(title: string, subtitle: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 480;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Card background.
    const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
    grad.addColorStop(0, 'rgba(20, 24, 60, 0.92)');
    grad.addColorStop(1, 'rgba(14, 16, 44, 0.92)');
    ctx.fillStyle = grad;
    roundRect(ctx, 12, 12, canvas.width - 24, canvas.height - 24, 28);
    ctx.fill();

    // Border.
    ctx.strokeStyle = 'rgba(180, 227, 255, 0.55)';
    ctx.lineWidth = 4;
    roundRect(ctx, 12, 12, canvas.width - 24, canvas.height - 24, 28);
    ctx.stroke();

    // Tag pill.
    ctx.fillStyle = 'rgba(159, 217, 255, 0.95)';
    roundRect(ctx, 36, 36, 280, 64, 20);
    ctx.fill();
    ctx.fillStyle = '#0d1224';
    ctx.font = 'bold 30px Inter, system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText('VIBEJAM 2026', 60, 70);

    // Title.
    ctx.shadowColor = 'rgba(159, 217, 255, 0.6)';
    ctx.shadowBlur = 22;
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 78px Inter, system-ui, sans-serif';
    ctx.fillText(title, 36, 200);

    // Subtitle (manual word-wrap for canvas).
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(212, 226, 255, 0.92)';
    ctx.font = '32px Inter, system-ui, sans-serif';
    wrapText(ctx, subtitle, 36, 280, canvas.width - 72, 42);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 4;
  return tex;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
): void {
  const words = text.split(' ');
  let line = '';
  let cy = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, cy);
      line = word;
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, cy);
}
